import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { AdornisMailer } from '@adornis/mails/server/mailer.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { getPasswordResetLink } from '@adornis/users/server/password.js';
import { html } from 'lit';
import { SmartTeamsFieldEngineer } from '../db/SmartTeamsFieldEngineer.js';
import { buildNSCEmail } from '../email-helpers.js';
import { normalizedEmailFilter } from './email-filter.js';

export const getCurrentFieldEngineerMustChangePassword = registerQuery({
  operationName: 'getCurrentFieldEngineerMustChangePassword',
  type: () => Boolean,
  params: [],
  options: { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } },
  resolve: () => async () => {
    const user = await CurrentUserInfo.getMyself()({ _id: 1 });
    if (!user?._id) return false;

    const collection = await getRawCollection<{ mustChangePassword?: boolean }>(SmartTeamsFieldEngineer._collectionName);
    const fieldEngineer = await collection.findOne({ userId: user._id }, { projection: { mustChangePassword: 1 } });

    return fieldEngineer?.mustChangePassword === true;
  },
});

export const clearMustChangePasswordFlag = registerMutation({
  operationName: 'clearMustChangePasswordFlag',
  type: () => Boolean,
  params: [],
  resolve: () => async () => {
    const user = await CurrentUserInfo.getMyself()({ _id: 1 });
    if (!user?._id) return false;

    const collection = await getRawCollection(SmartTeamsFieldEngineer._collectionName);
    const result = await collection.updateOne({ userId: user._id }, { $unset: { mustChangePassword: '' } });

    return result.modifiedCount > 0;
  },
});

export const sendCurrentUserPasswordResetEmail = registerMutation({
  operationName: 'sendCurrentUserPasswordResetEmail',
  type: () => Boolean,
  params: [{ name: 'email', type: () => String }],
  resolve: (email: string) => async () => {
    const users = await (
      await getRawCollection<EntityData<AdornisUser>>(AdornisUser._collectionName)
    )
      .find(normalizedEmailFilter(email), { projection: { _id: 1, email: 1 } })
      .limit(2)
      .toArray();
    if (users.length !== 1) return false;

    const user = users[0];
    if (!user?._id || !user.email) return false;

    const resetLink = await getPasswordResetLink('/change-password', {
      userID: user._id,
      userClass: user._class ?? 'AdornisUser',
    });

    const message = await Message.compose({
      subject: 'NSC Global: SmartTeams password reset',
      html: buildNSCEmail(
        html`<p>Hello,</p>
          <p>Use this link to set your new password:</p>
          <p><a href="${resetLink}" style="word-break: break-all;">${resetLink}</a></p>
          <p>If you did not request this, please ignore this email.</p>`,
      ),
    });

    await AdornisMailer.getInstance().sendMail(message, new Recipient(user.email));
    return true;
  },
});
